import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authApi, setTokens, clearTokens } from '../services/api'

interface AuthContextType {
  user: any | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithLink: (token: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem('adminToken')
    if (!token) {
      setLoading(false)
      return
    }
    try {
      const res = await authApi.getProfile()
      if (res.data.success && res.data.data) {
        setUser(res.data.data)
      } else {
        clearTokens()
        setUser(null)
      }
    } catch {
      clearTokens()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const login = async (email: string, password: string) => {
    const res = await authApi.login(email, password)
    if (res.data.success && res.data.data) {
      setTokens(res.data.data.accessToken, res.data.data.refreshToken)
      setUser(res.data.data.user)
    } else {
      throw new Error(res.data.message || 'خطا در ورود')
    }
  }

  /** ورود با لینک اختصاصی که مدیر برای کاربر ساخته است */
  const loginWithLink = useCallback(async (token: string) => {
    const res = await authApi.loginWithLink(token)
    if (res.data.success && res.data.data) {
      setTokens(res.data.data.accessToken, res.data.data.refreshToken)
      setUser(res.data.data.user)
    } else {
      throw new Error(res.data.message || 'لینک ورود نامعتبر است')
    }
  }, [])

  const logout = async () => {
    try {
      await authApi.logout()
    } catch {
      // ignore
    }
    clearTokens()
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithLink, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
